import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import queryString from "query-string";
import { getApiUrl } from "./getApiUrl";
import { decodeJwtPayload, isJustLoggedOut } from 'common/utils/auth';
// webview.ts 는 다른 모듈을 import 하지 않아 순환이 생기지 않는다
import { isNativeApp, requestNativeTokenRefresh, isNativeRefreshAvailable } from 'common/webview';

export const RequestMethod = {
    GET: 'GET',
    POST: 'POST',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
    PUT: 'PUT'
}

type RequestMethod = typeof RequestMethod[keyof typeof RequestMethod];

export type RequestProps = {
    method?: RequestMethod;
    url: string;
    query?: { [key: string]: string | number };
    headers?: any;
    params?: any;
    accessToken?: string;
    refreshToken?: string;
    fe?: boolean;
    skipAuth?: boolean;
};

// 토큰 갱신 중 여부
let isRefreshing = false;
// 토큰 갱신 대기 중인 요청들
let failedQueue: { resolve: (token: string) => void; reject: (error: any) => void }[] = [];

// 대기 중인 요청 처리
const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token!);
        }
    });
    failedQueue = [];
};

// JWT 만료 여부 확인 — base64url payload 디코드 후 exp 필드 검증
// 잘못된 형식이면 true(만료) 반환해 안전 측 처리.
//
// ⚠️ Latin-1 → UTF-8 재해석 필수. atob 만으로 JSON.parse 하면 닉네임 등
// 멀티바이트 문자가 깨져 SyntaxError → 정상 토큰을 만료로 오판 → 매 요청마다
// 불필요한 refresh → 결국 refresh 누적/실패 → 강제 로그아웃 회귀가 있었음.
const isAccessTokenExpired = (token: string): boolean => {
    try {
        const payload = decodeJwtPayload(token);
        if (typeof payload?.exp !== 'number') return false;
        // 5초 시간 보정 (clock skew)
        return payload.exp * 1000 < Date.now() + 5000;
    } catch {
        return true;
    }
};

// refresh 1회 시도. 성공 시 새 accessToken, 실패 시 null.
// authRejected: 서버가 refresh 토큰 자체를 거부(4xx)했는지 — 호출자의 재시도 판단용.
const attemptTokenRefresh = async (
    refreshTokenValue: string
): Promise<{ accessToken: string | null; authRejected: boolean }> => {
    try {
        const response = await axios.post(getApiUrl('ssoRefresh'), {
            refreshToken: refreshTokenValue
        });

        if (response.status === 200) {
            const newAccessToken = response.headers['accesstoken'] || response.data?.accessToken;
            const newRefreshToken = response.headers['refreshtoken'] || response.data?.refreshToken;

            // 진단 — 200 인데 토큰 헤더/바디 둘 다 없는 backend 결함성 응답을 명시적 실패로 취급.
            if (!newAccessToken) {
                console.warn('[refresh] HTTP 200 but no accesstoken — treating as failure', {
                    hasHeader: !!response.headers['accesstoken'],
                    hasBodyField: !!response.data?.accessToken,
                    responseDataKeys: response.data ? Object.keys(response.data) : null,
                });
                return { accessToken: null, authRejected: false };
            }

            localStorage.setItem("accessToken", newAccessToken);
            if (newRefreshToken) {
                localStorage.setItem("refreshToken", newRefreshToken);
            }

            // 네이티브 웹뷰면 새 토큰을 native storage 에도 동기화.
            // 이 sync 가 빠지면 native 의 위치 송신용 토큰이 만료된 옛 토큰으로 멈춰
            // wiz-lbs 호출이 401/skip(no_token) 으로 실패 (iOS 에서 회귀로 발견됨).
            try {
                if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
                    (window as any).ReactNativeWebView.postMessage(JSON.stringify({
                        type: 'SEND_TOKEN',
                        data: {
                            accessToken: newAccessToken,
                            refreshToken: newRefreshToken,
                            access_token: newAccessToken,
                            refresh_token: newRefreshToken,
                        },
                    }));
                }
            } catch {
                /* postMessage 실패해도 web 흐름은 계속 */
            }

            return { accessToken: newAccessToken, authRejected: false };
        }
        console.warn('[refresh] non-200', response.status, response.data);
        return { accessToken: null, authRejected: false };
    } catch (error: any) {
        const status = error?.response?.status;
        console.error('[refresh] threw', {
            message: error?.message,
            status,
            data: error?.response?.data,
        });
        /* 4xx 를 전부 "이 refresh 토큰은 죽었다" 로 읽으면 안 됩니다.
           그렇게 읽으면 게이트웨이의 서명 오류(401 Invalid gateway signature),
           잘못된 요청(400), 호출 제한 같은 일시적인 4xx 하나에도 토큰을 지우고
           로그아웃시킵니다 — "자꾸 로그인이 풀린다" 의 유력한 경로입니다.

           서버는 토큰이 진짜로 틀렸을 때 code 로 분명히 말해 줍니다.
           실측(개발 서버): 엉터리 토큰·서명 불일치 → 400 INVALID_CREDENTIALS,
           빈 토큰 → 400 BAD_REQUEST. 그 신호가 있을 때만 로그아웃합니다.

           나머지 4xx 는 'network' 로 둡니다. refresh 토큰 수명이 30일이고
           회전도 하지 않으므로(실측) 다음 기회에 다시 시도하면 됩니다. */
        const code = typeof error?.response?.data?.code === 'string'
            ? error.response.data.code
            : '';
        const tokenRejected = code === 'INVALID_CREDENTIALS' || code === 'BAD_REQUEST';
        if (typeof status === 'number' && status >= 400 && status < 500 && !tokenRejected) {
            console.warn('[refresh] 4xx 지만 토큰 문제는 아닙니다 — 세션 유지', { status, code });
        }
        return { accessToken: null, authRejected: tokenRejected };
    }
};

// 마지막 refresh 실패 사유 — 응답 인터셉터가 로그아웃 여부 판단에 사용.
//  'invalid'  = 서버가 refresh 토큰 자체를 거부(4xx) → 로그아웃
//  'network'  = 네트워크/5xx/응답이상 → 세션 유지(로그아웃 금지), 다음 요청 재시도
let refreshFailReason: 'invalid' | 'network' | null = null;
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

// 토큰 갱신 함수 (동시 호출 보호 — 다중 진입 시 같은 promise 공유)
let refreshTokenPromise: Promise<string | null> | null = null;
const refreshToken = async (): Promise<string | null> => {
    if (refreshTokenPromise) return refreshTokenPromise;

    refreshTokenPromise = (async () => {
        refreshFailReason = null;
        try {
            const startAccess = localStorage.getItem("accessToken");

            // ── 앱 안에서는 네이티브가 토큰의 주인이다 ──
            // 네이티브와 웹뷰가 같은 refresh 토큰을 들고 있는데 서버는 갱신 즉시
            // 옛 토큰을 지운다. 웹뷰가 직접 부르면 네이티브 토큰이 그 순간 죽고,
            // 네이티브는 다음 갱신에서 4xx 를 받아 clearAuth 로 로그아웃해 버린다.
            // 그래서 갱신은 네이티브에 맡기고 결과만 받아 쓴다.
            // 응답이 없으면(브릿지가 없는 구버전 앱) 아래에서 직접 갱신한다.
            if (isNativeApp()) {
                const fromNative = await requestNativeTokenRefresh();
                if (fromNative.accessToken && !isAccessTokenExpired(fromNative.accessToken)) {
                    return fromNative.accessToken;
                }

                if (fromNative.accessToken) {
                    // 네이티브가 만료된 토큰을 그대로 돌려줬다 = 네이티브도 네트워크
                    // 사유로 갱신에 실패했다는 뜻이다(ensureFreshAccessToken 이 그때
                    // 기존 토큰을 반환한다). 네이티브는 refresh 토큰을 쥐고 있으므로
                    // 웹이 직접 치면 그걸 폐기시킨다. 다음 기회에 다시 위임한다.
                    refreshFailReason = 'network';
                    console.warn('[refresh] 네이티브가 만료 토큰 반환 — 웹 직접 갱신 생략(세션 유지)');
                    return null;
                }

                // 응답조차 없었다면(구버전 앱/지연) 지금처럼 자제한다. 웹이 직접 갱신하면
                // 회전형 refresh 토큰이 폐기되어 네이티브가 다음 갱신에서 4xx → 로그아웃된다.
                if (!fromNative.answered && isNativeRefreshAvailable()) {
                    refreshFailReason = 'network';
                    console.warn('[refresh] 네이티브 무응답 — 웹 직접 갱신 생략(세션 유지)');
                    return null;
                }

                // 네이티브가 "줄 토큰이 없다" 고 응답했다. 쓸 수 있는 토큰이 있었다면
                // 그걸 돌려줬을 테니, 지금 네이티브에는 지킬 토큰이 없다 — 웹이 직접
                // 갱신해도 폐기시킬 대상이 없다. 아래로 내려가 직접 갱신하고, 성공하면
                // attemptTokenRefresh 가 SEND_TOKEN 으로 네이티브까지 복구시킨다.
                // 그 갱신마저 4xx 면 'invalid' 로 분류되어 정상 재로그인으로 이어진다.
                //
                // 이 갈래가 없던 동안 네이티브 저장소가 빈 기기는 로그인처럼 보이면서
                // 모든 요청이 401 로 끝나는 교착에 빠졌다(노트10 실기기 확인).
                if (fromNative.answered) {
                    console.warn('[refresh] 네이티브에 토큰 없음 — 웹이 직접 갱신한다');
                }
            }

            const sentToken = localStorage.getItem("refreshToken");
            if (!sentToken) {
                refreshFailReason = 'invalid';
                return null;
            }

            const first = await attemptTokenRefresh(sentToken);
            if (first.accessToken) return first.accessToken;

            // ── 토큰 회전 경합 대응 ──
            // dokocrew-front 웹뷰와 dokocrew_native 가 같은 회전형 refresh 엔드포인트를 각자 호출한다.
            // 한 쪽이 먼저 refresh 하면 기존 refresh 토큰은 서버에서 즉시 폐기되므로,
            // 두 번째 호출은 4xx(authRejected) 로 거부된다.
            // 이때 즉시 로그아웃하지 말고, 먼저 갱신한 쪽(네이티브/다른 탭)이 동기화해줄
            // 새 토큰이 storage 에 들어오길 잠깐 기다렸다 ① 새 accessToken 이 오면 그대로 사용,
            // ② 회전된 refreshToken 이 오면 그걸로 1회 재시도.
            if (first.authRejected) {
                const insideNative = typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;
                const maxWaitMs = insideNative ? 2500 : 800;
                let waited = 0;
                while (waited < maxWaitMs) {
                    await sleep(200);
                    waited += 200;
                    // ① 네이티브/다른 탭이 갱신한 유효한 access 토큰이 들어왔으면 그대로 사용
                    const newAccess = localStorage.getItem("accessToken");
                    if (newAccess && newAccess !== startAccess && !isAccessTokenExpired(newAccess)) {
                        console.warn('[refresh] 동기화된 새 accessToken 사용 (회전 경합)');
                        return newAccess;
                    }
                    // ② 회전된 refresh 토큰이 들어왔으면 그걸로 재시도
                    const rotated = localStorage.getItem("refreshToken");
                    if (rotated && rotated !== sentToken) {
                        console.warn('[refresh] 회전된 refreshToken 으로 재시도');
                        const retry = await attemptTokenRefresh(rotated);
                        if (retry.accessToken) return retry.accessToken;
                        break;
                    }
                }
            }

            // 갱신 실패 — 사유 기록 (네트워크면 로그아웃하지 않음)
            refreshFailReason = first.authRejected ? 'invalid' : 'network';
            return null;
        } finally {
            refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
};

/**
 * axios 외부 경로(STOMP connectHeaders 등)에서 fresh access token 을 보장.
 * 토큰이 만료되었으면 refresh 시도, 실패면 null 반환.
 */
export const ensureFreshAccessToken = async (): Promise<string | null> => {
    const cur = localStorage.getItem("accessToken");
    if (cur && !isAccessTokenExpired(cur)) return cur;
    if (!cur) return null;
    return await refreshToken();
};

// 인증 없이 접근 가능한 공개 경로 — 401이 발생해도 로그인 리다이렉트 안 함
const PUBLIC_PATH_PREFIXES = [
    '/flash/vote/',
    '/flash/add-place/',
    '/invitation/',
    '/cost/public/',
    '/login',
    '/sign-up',
    '/guest-login/',
];
const isOnPublicPath = (): boolean => {
    try {
        const path = window.location.pathname;
        if (PUBLIC_PATH_PREFIXES.some(p => path.startsWith(p))) return true;
        // 게스트 토큰이 있고, 토큰이 가리키는 모임의 상세/일정/정산 경로면 공개로 취급 (401 시 로그인 강제 리다이렉트 방지)
        const guestToken = sessionStorage.getItem('guestToken');
        const guestMeetingId = sessionStorage.getItem('guestMeetingId');
        if (guestToken && guestMeetingId && path.includes(guestMeetingId)) {
            return true;
        }
        return false;
    } catch {
        return false;
    }
};

// 로그인 페이지로 리다이렉트
const redirectToLogin = () => {
    // 공개 경로(비회원 접근 허용)에서는 토큰 만료여도 로그인 페이지로 튕기지 않음
    if (isOnPublicPath()) {
        return;
    }
    // 진단 — 마지막 redirect 사유/시각/토큰 상태를 sessionStorage 에 보존.
    // "가끔 로그인 페이지로 튕긴다" 회귀 재현 시 _lastAuthRedirect 확인.
    try {
        const cur = localStorage.getItem("accessToken");
        const ref = localStorage.getItem("refreshToken");
        const reason = (window as any).__lastAuthRedirectReason || 'unknown';
        sessionStorage.setItem('_lastAuthRedirect', JSON.stringify({
            at: new Date().toISOString(),
            reason,
            hadAccessToken: !!cur,
            hadRefreshToken: !!ref,
            accessExpired: cur ? isAccessTokenExpired(cur) : null,
            url: window.location.pathname,
        }));
        console.warn('[redirectToLogin]', reason, { hadAccess: !!cur, hadRefresh: !!ref });
    } catch {}
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    /* 앱에도 알립니다 — 네이티브가 자기 저장소를 정리할 수 있게.
       다만 그 메시지에 기대어 이동까지 맡기지는 않습니다. dokocrew_native 에
       TOKEN_EXPIRED 핸들러가 없어서, 토큰만 지워진 채 보던 화면에 그대로
       남았습니다(세션이 풀렸는데 메인탭에 머무르던 원인). */
    if (window.ReactNativeWebView) {
        try {
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'TOKEN_EXPIRED',
                data: {},
            }));
        } catch { /* 브릿지 문제로 이동까지 막히면 안 됩니다 */ }
    }

    /* 웹이든 앱이든 여기서 직접 로그인 화면으로 옮깁니다.
       replace 를 쓰는 이유 — 뒤로가기로 방금 떠난(이미 죽은) 화면에
       돌아가면 거기서도 모든 요청이 401 입니다. */
    if (window.location.pathname !== '/login') {
        window.location.replace('/login');
    }
};

// Instance 관련 설정
export const instance = axios.create({
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// Request Interceptor - 요청에 토큰 추가
const requestConfig = async (config: InternalAxiosRequestConfig) => {
    let accessToken = localStorage.getItem("accessToken");

    // ── 네이티브 앱 fallback ──
    // dokocrew_native/nest_native 는 앱 시작 시 injectedJavaScriptBeforeContentLoaded 로
    // window.__APP__.accessToken 을 미리 주입해두지만, WebView 첫 로드 순간 localStorage 는
    // 아직 비어 있을 수 있다 (SEND_TOKEN postMessage 는 페이지 mount 뒤 도착).
    // 이 gap 사이에 axios 가 요청을 보내면 header 없이 401 → refresh(refreshToken 도 없어 실패)
    // → redirectToLogin() 이 발동해 로그인 페이지로 튕기는 회귀가 있었다.
    // 여기서 native 주입 토큰을 localStorage 에 미러링해서 첫 요청부터 정상 인증 헤더가 붙게 한다.
    // 방금 로그아웃했다면 미러링하지 않습니다. window.__APP__ 은 네이티브가
    // 페이지 로드 전에 심은 값이라 로그아웃 직후에도 옥 토큰이 남아 있고,
    // 여기서 되살리면 요청 한 번만에도 로그인 상태로 돌아갑니다.
    if (!accessToken && !isJustLoggedOut()) {
        try {
            const app = (window as any).__APP__;
            if (app?.accessToken) {
                localStorage.setItem("accessToken", app.accessToken);
                if (app.refreshToken) localStorage.setItem("refreshToken", app.refreshToken);
                accessToken = app.accessToken;
            }
        } catch { /* window 접근 실패 무시 */ }
    }

    // 만료 사전 감지 — stale 토큰으로 401 받기 전에 미리 refresh
    // 백엔드 STOMP/REST 인증 강화에 대응 (6d922b3, e367042)
    if (accessToken && isAccessTokenExpired(accessToken)) {
        const newToken = await refreshToken();
        accessToken = newToken;
    }

    if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
        // 게스트 토큰이 있으면 X-Guest-Token 헤더로 전달 (BUG-019)
        // wiz-gateway 가 X-Guest-Token 을 통과시키도록 설정되어 있어야 게스트가
        // /api/v1/meetings/:id/members/guest 같은 공개 엔드포인트에서 본인 검증 가능
        try {
            const guestToken = sessionStorage.getItem('guestToken');
            if (guestToken) {
                config.headers['X-Guest-Token'] = guestToken;
            }
        } catch {}
    }

    return config;
};

// Response Interceptor - 401 에러 시 토큰 갱신 후 재요청
const responseErrorHandler = async (error: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 에러이고, 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
        // refresh 요청 자체가 실패한 경우 로그인 페이지로 이동
        if (originalRequest.url?.includes('/auth/refresh')) {
            (window as any).__lastAuthRedirectReason = 'refresh_endpoint_401';
            redirectToLogin();
            return Promise.reject(error);
        }

        // 토큰 갱신 중이면 대기열에 추가
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return instance(originalRequest);
            }).catch(err => {
                return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newToken = await refreshToken();

            if (newToken) {
                processQueue(null, newToken);
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return instance(originalRequest);
            } else if (refreshFailReason === 'network') {
                // 네트워크/서버 일시 오류 — 세션 유지(로그아웃 금지), 이 요청만 실패시키고 다음에 재시도.
                processQueue(error, null);
                (window as any).__lastAuthRedirectReason = 'refresh_network_keep_session';
                return Promise.reject(error);
            } else {
                processQueue(error, null);
                (window as any).__lastAuthRedirectReason = 'refresh_returned_null';
                redirectToLogin();
                return Promise.reject(error);
            }
        } catch (refreshError) {
            processQueue(refreshError, null);
            (window as any).__lastAuthRedirectReason = 'refresh_threw';
            redirectToLogin();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }

    return Promise.reject(error);
};

instance.interceptors.request.use(requestConfig);
instance.interceptors.response.use(
    (response) => response,
    responseErrorHandler
);

// 쿼리 파싱
const onParseQuery = (query?: { [key: string]: any } | string) => {
    if (!query) return '';
    if (typeof query === 'string') return `?${query}`;
    return '?' + queryString.stringify(query);
};

export const onRequest = async (props: RequestProps): Promise<AxiosResponse> => {
    let response: AxiosResponse;
    let axiosInstance: AxiosInstance = instance;

    try {
        response = await axiosInstance({
            method: props.method,
            url: props.url + onParseQuery(props.query),
            data: props.params,
            headers: props.headers,
        })
        return response;
    } catch (error: any) {
        return error.response || error;
    }
};
